import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import DrawArrowIcon from '@src/svgs/DrawArrowIcon';
import DrawFreehandIcon from '@src/svgs/DrawFreehandIcon';
import DrawLineIcon from '@src/svgs/DrawLineIcon';
import DrawRectangleIcon from '@src/svgs/DrawRectangleIcon';
import EyeClosedIcon from '@src/svgs/EyeClosedIcon';
import EyeOpenIcon from '@src/svgs/EyeOpenIcon';
import RedoIcon from '@src/svgs/RedoIcon';
import UndoIcon from '@src/svgs/UndoIcon';
import { ANNOTATION_INK_PALETTE } from '@src/utility/annotationInk';
import React from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../../common/tooltipStyles';

import type { ClipAnnotations } from './useClipAnnotations';
import type { StrokeTool } from '@src/types.ts/comment';

const TOOLS: ReadonlyArray<{
  tool: StrokeTool;
  label: string;
  Icon: React.FC<{ width?: string; height?: string; strokeColor?: string }>;
}> = [
  { tool: 'arrow', label: 'Draw Arrow', Icon: DrawArrowIcon },
  { tool: 'line', label: 'Draw Line', Icon: DrawLineIcon },
  { tool: 'rect', label: 'Draw Box', Icon: DrawRectangleIcon },
  { tool: 'freehand', label: 'Draw Freehand', Icon: DrawFreehandIcon },
];

type Props = {
  annotations: ClipAnnotations;
  /**
   * Leave drawing mode. The row is revealed by the composer's draw toggle, so
   * the way out belongs at its left edge — the back chevron, not a collapse
   * control on the far side.
   */
  onExit: () => void;
};

/**
 * Drawing controls, inside the comment composer (PLAT-11289).
 *
 * In the composer rather than floating over the video, for two reasons: drawing
 * is part of composing a comment, so the tools belong where the words are; and
 * it leaves the frame unobstructed, with no canvas-anchored toolbar to position
 * or clamp to the viewport.
 *
 * Selecting a tool arms the drawing surface. Selecting it again disarms, which
 * is the way back to a normal canvas without hunting for a separate "done".
 */
export const AnnotationControls: React.FC<Props> = ({
  annotations,
  onExit,
}) => {
  const {
    tool,
    setTool,
    color,
    setColor,
    undo,
    redo,
    canUndo,
    canRedo,
    drawingsVisible,
    setDrawingsVisible,
  } = annotations;

  return (
    <Row data-testid="annotation-controls">
      <ExitButton
        type="button"
        aria-label="Close drawing tools"
        data-testid="annotation-controls-exit"
        data-tooltip-id="annotation-controls-tooltip"
        data-tooltip-content="Done Drawing"
        onClick={onExit}
      >
        {/* One chevron in the icon set; rotate it a quarter turn to point back
            rather than adding a near-duplicate glyph. Kept square: the glyph's
            viewBox is 10x10, so a non-square box squashes it and thins the
            stroke. Heavier stroke because it renders small. */}
        <BackChevron>
          <ChevronDownIcon width="14" height="14" strokeWidth="2" />
        </BackChevron>
      </ExitButton>
      <ToolButton
        type="button"
        aria-pressed={!drawingsVisible}
        aria-label={drawingsVisible ? 'Hide drawings' : 'Show drawings'}
        data-testid="annotation-visibility"
        data-tooltip-id="annotation-controls-tooltip"
        data-tooltip-content={
          drawingsVisible ? 'Hide Drawings' : 'Show Drawings'
        }
        onClick={() => setDrawingsVisible(!drawingsVisible)}
      >
        {drawingsVisible ? (
          <EyeOpenIcon width="15" height="15" strokeColor="currentColor" />
        ) : (
          <EyeClosedIcon width="15" height="15" strokeColor="currentColor" />
        )}
      </ToolButton>
      <Divider />

      <>
        <Group>
          {TOOLS.map(({ tool: id, label, Icon }) => (
            <ToolButton
              key={id}
              type="button"
              $active={tool === id}
              aria-pressed={tool === id}
              aria-label={label}
              data-testid={`annotation-tool-${id}`}
              data-tooltip-id="annotation-controls-tooltip"
              data-tooltip-content={label}
              // Clicking the armed tool disarms it, handing the canvas back.
              onClick={() => setTool(tool === id ? null : id)}
            >
              <Icon
                width="16"
                height="16"
                strokeColor={
                  tool === id ? 'var(--accent)' : 'var(--text-secondary)'
                }
              />
            </ToolButton>
          ))}
        </Group>

        <Divider />

        <Group>
          {ANNOTATION_INK_PALETTE.map(ink => (
            <SwatchButton
              key={ink.hex}
              type="button"
              $active={color.toLowerCase() === ink.hex.toLowerCase()}
              aria-pressed={color.toLowerCase() === ink.hex.toLowerCase()}
              aria-label={`${ink.name} ink`}
              data-testid={`annotation-ink-${ink.name.toLowerCase()}`}
              data-tooltip-id="annotation-controls-tooltip"
              data-tooltip-content={ink.name}
              onClick={() => setColor(ink.hex)}
            >
              {/* The active swatch is marked by a ring and a size bump, not by
                    recolouring — it has to keep showing its own ink. */}
              <Swatch style={{ background: ink.hex }} />
            </SwatchButton>
          ))}
        </Group>

        <Divider />

        <Group>
          <ToolButton
            type="button"
            disabled={!canUndo}
            aria-label="Undo"
            data-testid="annotation-undo"
            data-tooltip-id="annotation-controls-tooltip"
            data-tooltip-content="Undo"
            onClick={undo}
          >
            <UndoIcon width="14" height="14" />
          </ToolButton>
          <ToolButton
            type="button"
            disabled={!canRedo}
            aria-label="Redo"
            data-testid="annotation-redo"
            data-tooltip-id="annotation-controls-tooltip"
            data-tooltip-content="Redo"
            onClick={redo}
          >
            <RedoIcon width="14" height="14" />
          </ToolButton>
        </Group>
      </>

      {ReactDOM.createPortal(
        <Tooltip
          id="annotation-controls-tooltip"
          style={COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </Row>
  );
};

export default AnnotationControls;

// Sized to share a single composer line with the Send button.
//
// `flex-shrink: 0` and `nowrap` together are what keep it on that line. As a
// flex child of the composer footer it was previously allowed to shrink below
// its natural width (`min-width: 0`), at which point `wrap` broke the controls
// onto a second row and left Send stranded beside both. The row is a fixed set
// of eleven small controls, so its natural width is the right width.
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  flex-shrink: 0;
`;

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  margin: 2px 3px;
  background: var(--border);
`;

const buttonReset = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

const ToolButton = styled.button<{ $active?: boolean }>`
  ${buttonReset}
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm, 6px);
  /* Pinned explicitly: the eye glyph draws with currentColor, so without this
     it inherited whatever the composer set and sat a shade off its neighbours. */
  color: ${props =>
    props.$active ? 'var(--accent)' : 'var(--text-secondary)'};
  /* Gold marks the selected tool: the design system reserves green for actions
     and never uses it as a selection indicator. */
  background: ${props =>
    props.$active
      ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
      : 'transparent'};

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const SwatchButton = styled.button<{ $active: boolean }>`
  ${buttonReset}
  /* 20px keeps a comfortable hit target; the 16px dot inside leaves exactly the
     2px gap the ring sits beyond, so the spacing is geometry rather than a
     stack of box-shadow layers faking a gap. */
  width: 20px;
  height: 20px;
  border-radius: 50%;
  /* Full --foreground: a part-opacity white read as gray against the panel.
     Thin and tight to the dot, so it marks the selection without competing
     with the ink it surrounds. */
  box-shadow: ${props =>
    props.$active ? '0 0 0 1.5px var(--foreground)' : 'none'};
  transition:
    transform 120ms ease,
    box-shadow 120ms ease;

  /* No lift on the active swatch — the ring already says which one it is, and
     scaling it moved the ring too. Hover keeps a nudge as click feedback. */
  &:hover {
    transform: scale(1.06);
  }
`;

const Swatch = styled.span`
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
`;

const ExitButton = styled.button`
  ${buttonReset}
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm, 6px);
  /* One step brighter than the tool glyphs: at this size --text-muted on the
     composer fill is close to invisible. */
  color: var(--text-secondary);

  &:hover {
    color: var(--foreground);
    background: var(--surface-elevated);
  }
`;

const BackChevron = styled.span`
  display: inline-flex;
  transform: rotate(90deg);

  /* A parent's blanket \`svg {}\` rule can win on equal specificity, so pin the
     size here too — square, to keep the glyph from squashing. */
  && svg {
    width: 14px;
    height: 14px;
  }
`;
